import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface RelatedItem {
  id: string
  title: string
  createdAt: string | null
  participants: string[]
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric' })
}

function MeetingLinkList({ items }: { items: RelatedItem[] }) {
  return (
    <div className="flex flex-col" style={{ gap: 4 }}>
      {items.map((m) => (
        <Link
          key={m.id}
          to={`/documents/${m.id}`}
          className="flex flex-col px-2 py-1.5 rounded-md"
          style={{ textDecoration: 'none', color: 'inherit' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--color-surface, #f5f6f8)' }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
        >
          <span className="text-sm font-medium" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--color-accent, #3b5bdb)' }}>
            {m.title}
          </span>
          <span className="text-xs text-tertiary">{formatDate(m.createdAt)}</span>
        </Link>
      ))}
    </div>
  )
}

/**
 * 議事録詳細の右カラム: 前回のMTG（同じ定例シリーズ）と関連MTG（タイトル/フォルダが近い会議）
 */
export function RelatedMeetings({ documentId }: { documentId: string }) {
  const { user, firebaseUser } = useAuth()
  const [previous, setPrevious] = useState<RelatedItem[]>([])
  const [related, setRelated] = useState<RelatedItem[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!firebaseUser || !user?.orgId || !documentId) return
    let cancelled = false
    ;(async () => {
      try {
        const token = await firebaseUser.getIdToken()
        const res = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/documents/${documentId}/related`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) return
        const data = await res.json()
        if (cancelled) return
        setPrevious(data.previous || [])
        setRelated(data.related || [])
      } catch {
        // ベストエフォート（表示しないだけ）
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => { cancelled = true }
  }, [firebaseUser, user?.orgId, documentId])

  if (!loaded || (previous.length === 0 && related.length === 0)) return null

  return (
    <div className="card" style={{ padding: 12 }}>
      {previous.length > 0 && (
        <div style={{ marginBottom: related.length > 0 ? 12 : 0 }}>
          <div className="text-sm font-medium text-primary" style={{ marginBottom: 6 }}>前回のMTG</div>
          <MeetingLinkList items={previous} />
        </div>
      )}
      {related.length > 0 && (
        <div>
          <div className="text-sm font-medium text-primary" style={{ marginBottom: 6 }}>関連MTG</div>
          <MeetingLinkList items={related} />
        </div>
      )}
    </div>
  )
}
